import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../AppIcon';
import { CalendarHeart as CalendarHeartP, ShieldCheck as ShieldCheckP, PauseCircle as PauseCircleP } from '@phosphor-icons/react';
import { Check, Star, ArrowRight, Loader2, CalendarHeart, ShieldCheck, PauseCircle } from 'lucide-react';
import WeddingDateDialog from '../billing/WeddingDateDialog';
import FreeToolsGap from './FreeToolsGap';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';

// Curated marketing bullets per tier — kept in the frontend so we can
// re-word without redeploying the backend. The tier keys must stay in
// sync with plan_catalog.PLAN_CATALOG keys.
const TIER_HIGHLIGHTS = {
  sweetheart: [
    'Wedding website + RSVP form',
    'Guest list up to 50',
    'Budget, Vendors, Timeline, Seating tools',
    'Registry aggregation page',
    '10 messages/mo with the AI Wedding Friend',
  ],
  beloved: [
    'Guest list up to 300',
    'Unlimited AI Wedding Friend',
    'RSVP Digest Reports for mom, planner, siblings',
    'Auto-decline non-responders + reminder chains',
    'Custom subdomain (yourname.\u200Byourweddingfriends.com)',
    'Broadcast email + save-the-date postcards at cost',
  ],
  concierge: [
    'Everything in Beloved',
    'Dedicated wedding phone number (AI answers 24/7)',
    'Concierge AI: your wedding line answers by phone',
    'Guest inbound calls and texts, AI-drafted',
    'Broadcast SMS + automated reminders',
    'Priority support',
  ],
  wedding_pass: [
    'Full Concierge feature set',
    'Coverage through wedding + 90 days',
    '$99 activation fee waived',
    'One payment. No subscription to remember',
  ],
};

const CURRENCY_LABEL = { usd: 'USD', cad: 'CAD' };
const CURRENCY_SYMBOL = { usd: '$', cad: 'C$' };

// Best-effort market default from browser locale. Only US/CA are wired at
// launch; everyone else defaults to USD.
function detectCurrency() {
  try {
    const lang = (navigator.language || '').toLowerCase();
    if (lang.endsWith('-ca') || lang.includes('fr-ca')) return 'cad';
    // Timezone hint for cases where language is generic English
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|Montreal|St_Johns|Regina)/i.test(tz)) return 'cad';
  } catch (e) { /* ignore */ }
  return 'usd';
}

export default function Pricing() {
  const [pricing, setPricing] = useState(null);
  const [currency, setCurrency] = useState(detectCurrency());
  const [submitting, setSubmitting] = useState(null);
  const [dateAsk, setDateAsk] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/pricing');
        setPricing(data);
      } catch (e) { /* ignore, keep loading state */ }
    })();
  }, []);

  const plans = useMemo(() => pricing?.plans || [], [pricing]);

  const priceFor = (plan, cadence = 'month') => {
    const priceCat = plan.prices?.[currency];
    if (!priceCat) return null;
    return priceCat[cadence] || null;
  };

  const startCheckout = async ({ tier, cadence, includeSetupFee = true, dateUnknown = false }) => {
    if (!user) {
      // Save intent so signup can pick up where they left off
      try { localStorage.setItem('post_signup_checkout', JSON.stringify({ tier, cadence, currency })); } catch {}
      navigate(`/auth?next=/dashboard`);
      return;
    }
    setSubmitting(`${tier}-${cadence}`);
    try {
      const { data } = await client.post('/payments/checkout', {
        tier,
        currency,
        cadence,
        include_setup_fee: includeSetupFee,
        date_unknown: dateUnknown,
        origin_url: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      setSubmitting(null);
      if (e.code === 'wedding_date_required') { setDateAsk({ tier, cadence, includeSetupFee }); return; }
      toast({ title: 'Couldn\'t start checkout', description: e.message });
    }
  };

  const submitDate = async ({ date, unknown }) => {
    const args = dateAsk;
    try {
      if (date) await client.put('/profile', { weddingDate: date });
      setDateAsk(null);
      startCheckout({ ...args, dateUnknown: !!unknown });
    } catch (e) {
      toast({ title: 'Could not save your date', description: e.message });
    }
  };

  if (!pricing) {
    return (
      <section id="pricing" className="py-24 lg:py-32 bg-[#FBF7F2]">
        <div className="max-w-6xl mx-auto px-6 text-center text-[#9A8A7E]">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading plans…
        </div>
      </section>
    );
  }

  const fmt = (amountMinor) => {
    if (amountMinor === null || amountMinor === undefined) return '—';
    return `${CURRENCY_SYMBOL[currency]}${(amountMinor / 100).toFixed(0)}`;
  };

  return (
    <section id="pricing" className="py-24 lg:py-32 bg-[#FBF7F2]" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <p className="section-eyebrow">{pricing.copy?.positioning}</p>
          <h2 className="font-serif text-4xl lg:text-5xl mt-3 text-[#2A2A2A] leading-tight">
            {pricing.copy?.headline || 'Plans built for calmer weddings.'}
          </h2>
          <p className="mt-4 text-[#4A4A4A]">Free to start. Monthly plans end on their own after your wedding. Or pay once with a Wedding Pass.</p>
        </div>

        <div data-testid="pricing-promises" className="flex flex-wrap justify-center gap-3 mb-8">
          {[
            [CalendarHeartP, 'Ends automatically after your wedding', 'No subscription to remember to cancel.', 'wine'],
            [ShieldCheckP, 'Never more than the cap', `Beloved tops out at ${CURRENCY_SYMBOL[currency]}${currency === 'cad' ? 199 : 149} total.`],
            [PauseCircleP, 'Pause anytime', 'Up to 3 months during quiet stretches.', 'sky'],
          ].map(([Icon, title, sub, tone = 'sage'], i) => (
            <div key={i} data-testid={`pricing-promise-${i}`} className={`group flex items-start gap-3 rounded-2xl px-5 py-3.5 text-left ${i === 0 ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'bg-white border border-[#EFE3D8]'}`}>
              <AppIcon icon={Icon} tone={tone} size="sm" interactive reveal delay={i * 90} />
              <div>
                <p className={`text-sm font-medium ${i === 0 ? '' : 'text-[#2A2A2A]'}`}>{title}</p>
                <p className={`text-xs ${i === 0 ? 'text-[#F4D6CE]' : 'text-[#6A6A6A]'}`}>{sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Currency toggle */}
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex rounded-full bg-white border border-[#EFE3D8] p-1" role="tablist" aria-label="Currency">
            {['usd', 'cad'].map((cur) => (
              <button
                key={cur}
                data-testid={`currency-${cur}`}
                onClick={() => setCurrency(cur)}
                className={`px-4 py-1.5 text-xs rounded-full transition-colors ${currency === cur ? 'bg-[#7B2C3B] text-white' : 'text-[#6A6A6A]'}`}
              >{CURRENCY_LABEL[cur]}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((p) => {
            const highlights = TIER_HIGHLIGHTS[p.tier] || [];
            const isFree = p.is_free;
            const isPass = p.is_one_time;
            const monthly = priceFor(p, 'month');
            const oneTime = priceFor(p, 'one_time');
            const isPopular = p.tier === 'beloved';
            const priceAmt = monthly?.amount ?? oneTime?.amount ?? null;
            const cadenceLabel = isFree ? 'forever' : monthly ? '/mo' : (isPass ? 'once' : '');
            const buttonLabel = isFree
              ? 'Start free'
              : isPass
                ? 'Choose Wedding Pass'
                : p.tier === 'concierge'
                  ? 'Start Concierge'
                  : 'Start Beloved';
            const disabled = !isFree && priceAmt === null;
            const key = isFree ? 'sweetheart' : (isPass ? `${p.tier}-one_time` : `${p.tier}-month`);
            const submittingThis = submitting === key;
            return (
              <div
                key={p.tier}
                data-testid={`plan-card-${p.tier}`}
                className={`relative rounded-3xl p-7 transition-all duration-300 ${
                  isPopular
                    ? 'bg-[#2A1F1B] text-[#FBF7F2] lg:scale-105 shadow-2xl'
                    : 'soft-card hover:translate-y-[-3px]'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#7B2C3B] text-[#FBF7F2] text-[10px] uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <Star className="w-3 h-3 fill-[#FBF7F2]" /> Most popular
                  </div>
                )}
                <p className={`text-xs uppercase tracking-widest ${isPopular ? 'text-[#EFC9B0]' : 'text-[#B8896C]'}`}>{p.name}</p>
                <p className={`text-xs italic mt-1 ${isPopular ? 'text-[#DAC7B8]' : 'text-[#6A6A6A]'}`}>{p.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-serif text-4xl">{isFree ? '$0' : fmt(priceAmt)}</span>
                  <span className={`text-xs ${isPopular ? 'text-[#DAC7B8]' : 'text-[#9A8A7E]'}`}>{cadenceLabel}</span>
                </div>
                {p.tier === 'beloved' && (
                  <p data-testid="plan-cap-beloved" className="text-[11px] mt-1 text-[#EFC9B0]">
                    or {fmt(priceFor(p, 'year')?.amount)}/yr · never more than {fmt(p.price_cap?.[currency])} total
                  </p>
                )}
                {p.tier === 'concierge' && (
                  <p data-testid="plan-cap-concierge" className={`text-[11px] mt-1 ${isPopular ? 'text-[#DAC7B8]' : 'text-[#9A8A7E]'}`}>
                    Plus one-time {fmt(p.setup_prices?.[currency]?.one_time?.amount)} activation · never more than {fmt(p.price_cap?.[currency])} total, same as the Wedding Pass
                  </p>
                )}
                {!isFree && !isPass && (
                  <p className={`text-[11px] mt-1 inline-flex items-center gap-1 ${isPopular ? 'text-[#DAC7B8]' : 'text-[#9A8A7E]'}`}>
                    <CalendarHeart className="w-3 h-3" /> Ends automatically after your wedding
                  </p>
                )}
                <ul className="mt-5 space-y-2">
                  {highlights.map((h, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${isPopular ? 'text-[#F1E4D6]' : 'text-[#3A3A3A]'}`}>
                      <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isPopular ? 'text-[#EFC9B0]' : 'text-[#7B2C3B]'}`} />
                      <span className="min-w-0 [overflow-wrap:anywhere]">{h}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-cta-${p.tier}`}
                  disabled={disabled || submittingThis}
                  onClick={() => {
                    if (isFree) { navigate('/auth?next=/dashboard'); return; }
                    startCheckout({
                      tier: p.tier,
                      cadence: isPass ? 'one_time' : 'month',
                      includeSetupFee: p.tier === 'concierge' ? p.setup_default_enabled !== false : false,
                    });
                  }}
                  className={`mt-6 w-full py-3 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 transition-transform hover:translate-y-[-1px] disabled:opacity-50 ${
                    isPopular
                      ? 'bg-[#EFC9B0] text-[#2A1F1B] hover:bg-[#F4D6CE]'
                      : 'bg-[#7B2C3B] text-[#FBF7F2] hover:bg-[#5F1F2C]'
                  }`}
                >
                  {submittingThis ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{buttonLabel} <ArrowRight className="w-3.5 h-3.5" /></>}
                </button>
                {p.tier === 'concierge' && (
                  <p className={`text-[10px] mt-2 text-center ${isPopular ? 'text-[#DAC7B8]' : 'text-[#9A8A7E]'}`}>{pricing.copy?.concierge_geo}</p>
                )}
                {disabled && (
                  <p className={`text-[10px] mt-2 text-center ${isPopular ? 'text-[#DAC7B8]' : 'text-[#9A8A7E]'}`}>Not available in {CURRENCY_LABEL[currency]} yet.</p>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-[10px] text-[#9A8A7E] max-w-2xl mx-auto">
          {pricing.copy?.reasonable_use} · {pricing.copy?.tax_note} · Monthly plans stop billing 90 days after your wedding date (or 18 months after signup if no date is set). Caps exclude tax.
        </p>

        <FreeToolsGap />
        <WeddingDateDialog open={!!dateAsk} onOpenChange={(o) => !o && setDateAsk(null)} onSubmit={submitDate} busy={!!submitting} />
      </div>
    </section>
  );
}
